import { createSlice } from "@reduxjs/toolkit";
import { isEmpty } from "lodash";
// ⚠️ `userLogout` / `supportLogout` exist ONLY in LoginSlice/index.desktop.ts — hazard 6.
import { supportLogout, userLogout } from "../LoginSlice";
// ⚠️ These two ARE platform-defined and this file knows its platform, so it names the variant —
// hazard 16's rule for a platform file (the inverse of ./shared.ts, which must import bare).
import { LOCALSTORAGE_KEYS } from "../../../utils/constants";
import { setLocalStorage } from "../../../utils/localStorage";
import { OLD_STATE_KEY } from "../../../utils/migrateState";
import { sharedInitialState, sharedReducers } from "./shared";
import type { SharedPrinterState } from "./shared";

/**
 * ⚠️ `packages/core`'s tsconfig has no `DOM` lib (it must stay RN-safe), so both browser globals
 * are declared locally — same treatment as `helpers/commonHelpers/index.desktop.ts`.
 *
 * `localStorage` is declared rather than routed through core's `utils/localStorage`
 * `getLocalStorage` (which IS `localStorage.getItem`, so the swap would have been behaviour-
 * identical): `--verify` compares reducer BODIES and flagged the substitution, correctly. This
 * file is web-only and never evaluates on mobile, so the global is safe here.
 */
declare function structuredClone<T>(value: T): T;
declare const localStorage: { getItem(key: string): string | null };

/** Web adds `localPrinters`; mobile has no such field. NOT exported — neither app exported it. */
interface Props extends SharedPrinterState {
  localPrinters: any;
}

const initialState: Props = {
  ...sharedInitialState,
  localPrinters: [],
};

export const PrinterSlice = createSlice({
  name: "printer",
  initialState,
  reducers: {
    ...sharedReducers,
    // ── The 2 below are web-only.
    saveLocalPrintersLocally : (state, action) => {
      state.localPrinters = action.payload
    },
    // ⚠️ Byte-identical to mobile's `updateDefaultPrinter`, under a different NAME — so it is a
    //    different action type and cannot be shared without renaming one app's action. See
    //    ./shared.ts.
    setDefaultPrinter: (state, action) => {
      state.defaultPrinter = action.payload;
      setLocalStorage(LOCALSTORAGE_KEYS.defaultPrinter, JSON.stringify(action.payload)); // TODO: to be removed once the default printer issue is fixed.
    },
    // ── Genuinely divergent. Mobile stores the payload and mirrors `defaultPrinter` to storage;
    //    web additionally (a) bails out early on a device that still holds the pre-redux-persist
    //    blob, and (b) folds the legacy `defaultPrinterDetails` key into `defaultPrinter`.
    updatePrinterSlice: (state, action) => {
      const oldStateStr = localStorage.getItem(OLD_STATE_KEY);
      if (!!oldStateStr) {
        return action.payload;
      }
      const updatedPrinterSliceState = structuredClone(action.payload || {});
      const printerOldRedux = updatedPrinterSliceState?.defaultPrinterDetails || {};
      const printerNewRedux = updatedPrinterSliceState?.defaultPrinter || {};
      let defaultPrinter = {};
      if (!isEmpty(printerNewRedux)) {
        defaultPrinter = printerNewRedux;
      } else if (!isEmpty(printerOldRedux)) {
        defaultPrinter = printerOldRedux;
        updatedPrinterSliceState.defaultPrinter = printerOldRedux;
      }
      delete updatedPrinterSliceState.defaultPrinterDetails;
      setLocalStorage(LOCALSTORAGE_KEYS.defaultPrinter, JSON.stringify(defaultPrinter)); // TODO: to be removed once the default printer issue is fixed.
      return updatedPrinterSliceState;
    },
  },
  // ⚠️ WEB-ONLY, and both bodies are EMPTY — these cases are declared and do nothing. Preserved
  //    verbatim: they are part of the declared surface `--verify` checks, and removing them is a
  //    behaviour question (does logout intend to clear printers?) that this uplift does not
  //    answer. Mobile declares no extraReducers at all. Flagged in the ledger.
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {

      })
      .addCase(supportLogout, (state) => {

      });
  },
});

export const {
  savePrinter,
  fetchAllPrinter,
  deletePrinter,
  storeAllPrinters,
  emptyPrinters,
  addUSBPrinter,
  addLocalPrinter,
  saveLocalPrintersLocally,
  setDefaultPrinter,
  clearDefaultPrinter,
  updatePrinterSlice,
  getUnprintedTickets,
  getLabelPrintStatus,
} = PrinterSlice.actions;

export default PrinterSlice.reducer;
